import Component from '@glimmer/component'
import { t } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { CourseProgressTotalsReport } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/totals/report'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import { ChartTooltip } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/chart-tooltip'
import { NoReportValue } from 'teacher-ui-client/components/reporting/no-report-value'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import { array } from '@ember/helper'

export interface CourseProgressTotalsReportData {
  course_progress_lessons_results_totals: TotalsReportDataForGrade[]
}

export interface TotalsReportDataForGrade {
  grade_position: number
  summary: {
    total_count: Nullable<number>
    student_count: Nullable<number>
    student_average: Nullable<number>
  }
  months: {
    count: number // the number of lessons completed this month
    month: string
  }[]
}

interface Signature {
  Args: {
    uiScope: UIScope
    period: NormalizedInterval
  }
}

export class CourseProgressTotalsReportLoader extends Component<Signature> {
  @service reporting!: ReportingService

  fetchReport = trackedFunction(this, async () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'course-progress/lessons-results/totals',
    })
    const query = this.reporting.buildQuery({ period: this.args.period })
    const report = await this.reporting.fetchReport<CourseProgressTotalsReportData>(url, query)
    return report.course_progress_lessons_results_totals?.length ? report.course_progress_lessons_results_totals : null
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#each this.latestReport as |totalsReportDataForGrade|}}
      <ContentTile class="relative h-76 {{if this.fetchReport.isPending 'opacity-50'}}">
        <ChartTooltip @text={{t "reporting.tooltips.courseProgressTotals"}} />
        <CourseProgressTotalsReport
          @scopeName={{@uiScope.scope}}
          @totalsReportDataForGrade={{totalsReportDataForGrade}}
        />
      </ContentTile>
    {{else}}
      <ContentTile class="p-4">
        <NoReportValue @reportStates={{array this.fetchReport}} />
      </ContentTile>
    {{/each}}
  </template>
}
